import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ButtonControl, FieldControl, Loading } from "spark-core-dx/components";
import { SystemTypes } from "spark-core-dx/services";
import { useResourceDomainContext } from "../../../ResourceDomainProvider";
import {
  buildWizardValidationError,
  getId,
  getOperationItem,
  getOperationItems,
  getRunSectionStepInputValues,
  getValue,
  readValue,
  requireSuccessfulServiceResponse
} from "../resourceConfigWizardUtils";
import {
  ENTITY_SCHEMA_MODE_DESTINATION_SCHEMA,
  ENTITY_SCHEMA_MODE_EDI,
  getEntitySchemaMode,
  getEntitySchemaModeLabel,
  getSchemaDisplayName,
  getSchemaIdFromValues,
  getSchemaStepValues,
  loadDestinationSchemaOptions
} from "./entitySchemaStepUtils";
import { LinkedStepHeader } from "./WizardStepCommon";

const option = (text, value = text) => ({ Text: text, Value: value });
const FALLBACK_EDI_TRANSACTION_SET_OPTIONS = [
  ["180", "Return Merchandise Authorization"],
  ["204", "Motor Carrier Load Tender"],
  ["210", "Motor Carrier Freight Details and Invoice"],
  ["211", "Motor Carrier Bill of Lading"],
  ["214", "Transportation Carrier Shipment Status Message"],
  ["215", "Motor Carrier Pick-up Manifest"],
  ["270", "Health Care Eligibility/Benefit Inquiry"],
  ["271", "Health Care Eligibility/Benefit Information"],
  ["276", "Health Care Claim Status Request"],
  ["277", "Health Care Claim Status Notification"],
  ["310", "Freight Receipt and Invoice"],
  ["315", "Status Details (Ocean)"],
  ["753", "Request for Routing Instructions"],
  ["754", "Routing Instructions"],
  ["810", "Invoice"],
  ["812", "Credit/Debit Adjustment"],
  ["816", "Organizational Relationships"],
  ["820", "Payment Order / Remittance Advice"],
  ["821", "Financial Information Reporting"],
  ["824", "Application Advice"],
  ["830", "Planning Schedule with Release Capability"],
  ["832", "Price/Sales Catalog"],
  ["834", "Benefit Enrollment and Maintenance"],
  ["835", "Health Care Claim Payment/Advice"],
  ["837", "Health Care Claim"],
  ["845", "Price Authorization Acknowledgment / Status"],
  ["846", "Inventory Inquiry/Advice"],
  ["848", "Material Safety Data Sheet"],
  ["850", "Purchase Order"],
  ["852", "Product Activity Data"],
  ["855", "Purchase Order Acknowledgment"],
  ["856", "Advance Ship Notice"],
  ["857", "Shipment and Billing Notice"],
  ["860", "Purchase Order Change Request - Buyer Initiated"],
  ["861", "Receiving Advice / Acceptance Certificate"],
  ["862", "Shipping Schedule"],
  ["865", "Purchase Order Change Acknowledgment / Request"],
  ["867", "Production Sequence"],
  ["870", "Order Status Report"],
  ["875", "Grocery Products Purchase Order"],
  ["876", "Grocery Products Invoice"],
  ["877", "Manufacturer Coupon Redemption Detail"],
  ["878", "Product Authorization/De-authorization"],
  ["879", "Price Information"],
  ["881", "Manufacturer Coupon Redemption Summary"],
  ["882", "Direct Store Delivery Summary Information"],
  ["886", "Customer Call Reporting"],
  ["887", "Coupon Notification"],
  ["888", "Item Maintenance"],
  ["889", "Promotion Announcement"],
  ["940", "Warehouse Shipping Order"],
  ["943", "Warehouse Stock Transfer Shipment Advice"],
  ["944", "Warehouse Stock Transfer Receipt Advice"],
  ["945", "Warehouse Shipping Advice"],
  ["947", "Inventory Adjustment Advice"],
  ["990", "Response to a Load Tender"],
  ["997", "Functional Acknowledgment"],
  ["999", "Implementation Acknowledgment"]
].map(([value, name]) => option(`${value} - ${name}`, value));

const optionValue = (entry) => {
  if (entry && typeof entry === "object" && !Array.isArray(entry)) {
    return readValue(entry, "Value", "value", "Id", "id", "Text", "text") ?? "";
  }
  return entry;
};

const normalizeMultiSelectValue = (value) => {
  if (Array.isArray(value)) {
    return value.map((entry) => String(optionValue(entry) || "").trim()).filter(Boolean);
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return [];
    if (trimmed.startsWith("[")) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) return normalizeMultiSelectValue(parsed);
      } catch {
        return [];
      }
    }
    return trimmed.split(",").map((entry) => entry.trim()).filter(Boolean);
  }
  return value === null || value === undefined || value === "" ? [] : [String(value).trim()].filter(Boolean);
};

const parseOptionsJson = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value !== "string" || !value.trim()) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const getFieldDefinition = (fields, fieldName) =>
  (fields || []).find((field) => String(readValue(field, "FieldName", "fieldName") || "").toLowerCase() === fieldName.toLowerCase());

const getFieldOptions = (fields, fieldName, fallbackOptions) => {
  const field = getFieldDefinition(fields, fieldName);
  const options = parseOptionsJson(readValue(field, "DataJson", "dataJson", "Options", "options"));
  return options.length ? options : fallbackOptions;
};

const multiSelectValuesEqual = (left, right) => {
  const leftValues = normalizeMultiSelectValue(left);
  const rightValues = normalizeMultiSelectValue(right);
  return leftValues.length === rightValues.length && leftValues.every((value, index) => value === rightValues[index]);
};

const defaultSchemaName = (wizard) => {
  const resourceDetailsValues = getRunSectionStepInputValues(wizard, "basics", "resource-details");
  const configuration = readValue(wizard, "ResourceConfiguration", "resourceConfiguration") || {};
  return readValue(resourceDetailsValues, "DisplayName", "displayName", "ConfigurationName", "configurationName")
    || readValue(configuration, "DisplayName", "displayName", "Name", "name")
    || readValue(wizard, "ConfigurationName", "configurationName")
    || readValue(wizard, "ResourceName", "resourceName")
    || "Entity Schema";
};

const fieldValue = (nextValue) => {
  if (nextValue && typeof nextValue === "object" && !Array.isArray(nextValue)) {
    return readValue(nextValue, "Value", "value", "Text", "text") ?? "";
  }
  return nextValue ?? "";
};

const fieldMetadata = ({ fieldName, displayName, fieldType, options, description = "", placeHolder = "", isRequired = false }) => ({
  FieldName: fieldName,
  DisplayName: displayName,
  FieldType: fieldType,
  Description: description,
  DataJson: options || [],
  PlaceHolder: placeHolder,
  IsRequired: isRequired
});

const buildInitialForm = (wizard, savedValues = {}) => ({
  SchemaId: getSchemaIdFromValues(wizard, savedValues),
  SchemaName: readValue(savedValues, "SchemaName", "schemaName", "name") || defaultSchemaName(wizard),
  SchemaDescription: readValue(savedValues, "SchemaDescription", "schemaDescription", "desc") || "",
  CreateNewSchemaVersion: readValue(savedValues, "CreateNewSchemaVersion", "createNewSchemaVersion", "nsv") === true,
  EdiFormat: readValue(savedValues, "EdiFormat", "ediFormat", "ediformat") || "X12",
  EdiDirection: readValue(savedValues, "EdiDirection", "ediDirection", "edidirection") || "Outbound",
  EdiTransactionSets: normalizeMultiSelectValue(readValue(savedValues, "EdiTransactionSets", "ediTransactionSets", "ts")),
  EdiSchemaShape: readValue(savedValues, "EdiSchemaShape", "ediSchemaShape", "sm") || "All",
  DestinationSchemaSelections: normalizeMultiSelectValue(readValue(
    savedValues,
    "DestinationSchemaSelections",
    "destinationSchemaSelections",
    "SelectedDestinationSchemas",
    "selectedDestinationSchemas",
    "dss"
  ))
});

const areFormValuesEqual = (left = {}, right = {}) =>
  left.SchemaId === right.SchemaId
  && left.SchemaName === right.SchemaName
  && left.SchemaDescription === right.SchemaDescription
  && left.CreateNewSchemaVersion === right.CreateNewSchemaVersion
  && left.EdiFormat === right.EdiFormat
  && left.EdiDirection === right.EdiDirection
  && multiSelectValuesEqual(left.EdiTransactionSets, right.EdiTransactionSets)
  && left.EdiSchemaShape === right.EdiSchemaShape
  && multiSelectValuesEqual(left.DestinationSchemaSelections, right.DestinationSchemaSelections);

const getFormKey = (form = {}) => JSON.stringify({
  SchemaId: form.SchemaId || "",
  SchemaName: form.SchemaName || "",
  SchemaDescription: form.SchemaDescription || "",
  CreateNewSchemaVersion: form.CreateNewSchemaVersion === true,
  EdiFormat: form.EdiFormat || "",
  EdiDirection: form.EdiDirection || "",
  EdiTransactionSets: normalizeMultiSelectValue(form.EdiTransactionSets).join("|"),
  EdiSchemaShape: form.EdiSchemaShape || "",
  DestinationSchemaSelections: normalizeMultiSelectValue(form.DestinationSchemaSelections).join("|")
});

const loadSchemaById = async (schemaMgr, schemaId, coreState) => {
  if (!schemaId) return null;
  if (schemaMgr?.getItem) {
    const result = await schemaMgr.getItem(schemaId);
    requireSuccessfulServiceResponse(result, "Linked Entity Schema could not be loaded.", coreState);
    return getOperationItem(result) || null;
  }
  if (schemaMgr?.Query) {
    const result = await schemaMgr.Query({
      model: "x => x.IsDeleted == false && (x._id == schemaId || x.SchemaId == schemaId || x.UniqueId == schemaId)",
      params: { schemaId },
      id: schemaId
    });
    requireSuccessfulServiceResponse(result, "Linked Entity Schema could not be loaded.", coreState);
    return getOperationItems(result).find((schema) =>
      getId(schema) === schemaId
      || getValue(schema, "SchemaId", "schemaId", "UniqueId", "uniqueId") === schemaId
    ) || null;
  }
  throw new Error("Schema service is not available.");
};

const EntitySchemaConfiguratorStep = ({ context, onChange, onSave, onContinue, registerStepController }) => {
  const { wizard, values = {}, readOnly, saving, fields = [] } = context;
  const resourceDomainContext = useResourceDomainContext();
  const coreState = resourceDomainContext?.coreState;
  const schemaMgr = resourceDomainContext?.managers?.schema;
  const externalSystemMgr = resourceDomainContext?.managers?.externalSystem;
  const entityObjectMgr = resourceDomainContext?.managers?.entityObject;
  const resourceConfigurationMgr = resourceDomainContext?.managers?.resourceConfiguration;
  const mode = getEntitySchemaMode(wizard, values, { preferSavedSetupMode: true });
  const savedValues = useMemo(() => getSchemaStepValues(wizard, values), [values, wizard]);
  const savedForm = buildInitialForm(wizard, savedValues);
  const savedFormKey = getFormKey(savedForm);
  const schemaManagerReady = Boolean(schemaMgr?.getItem || schemaMgr?.Query);
  const destinationSchemaManagerReady = Boolean(externalSystemMgr?.Query && entityObjectMgr?.Query && resourceConfigurationMgr?.Query);
  const ediTransactionSetOptions = useMemo(
    () => getFieldOptions(fields, "EdiTransactionSets", FALLBACK_EDI_TRANSACTION_SET_OPTIONS),
    [fields]
  );
  const [schema, setSchema] = useState(null);
  const [loading, setLoading] = useState(() => Boolean(savedForm.SchemaId));
  const [localError, setLocalError] = useState("");
  const [destinationSchemaOptions, setDestinationSchemaOptions] = useState([]);
  const [destinationSchemaLoading, setDestinationSchemaLoading] = useState(false);
  const [form, setForm] = useState(() => savedForm);
  const formRef = useRef(form);
  const schemaRef = useRef(schema);
  const savedValuesRef = useRef(savedValues);
  const savedFormRef = useRef(savedForm);
  const modeRef = useRef(mode);
  const schemaMgrRef = useRef(schemaMgr);
  const externalSystemMgrRef = useRef(externalSystemMgr);
  const entityObjectMgrRef = useRef(entityObjectMgr);
  const resourceConfigurationMgrRef = useRef(resourceConfigurationMgr);
  const wizardRef = useRef(wizard);
  const coreStateRef = useRef(coreState);
  const schemaLoadKeyRef = useRef("");
  const destinationSchemaLoadKeyRef = useRef("");
  formRef.current = form;
  schemaRef.current = schema;
  savedValuesRef.current = savedValues;
  savedFormRef.current = savedForm;
  modeRef.current = mode;
  schemaMgrRef.current = schemaMgr;
  externalSystemMgrRef.current = externalSystemMgr;
  entityObjectMgrRef.current = entityObjectMgr;
  resourceConfigurationMgrRef.current = resourceConfigurationMgr;
  wizardRef.current = wizard;
  coreStateRef.current = coreState;

  useEffect(() => {
    setForm((previous) => {
      const next = savedFormRef.current;
      const merged = {
        ...previous,
        ...next,
        SchemaName: next.SchemaName || previous.SchemaName || defaultSchemaName(wizard),
        SchemaDescription: next.SchemaDescription ?? previous.SchemaDescription ?? "",
        CreateNewSchemaVersion: next.CreateNewSchemaVersion
      };
      return areFormValuesEqual(previous, merged) ? previous : merged;
    });
  }, [savedFormKey, wizard]);

  useEffect(() => {
    let mounted = true;
    const loadLinkedSchema = async () => {
      const schemaIdToLoad = formRef.current.SchemaId;
      if (!schemaIdToLoad) {
        schemaLoadKeyRef.current = "";
        setSchema(null);
        setLoading(false);
        setLocalError("");
        return;
      }
      if (!schemaManagerReady) {
        setLoading(false);
        setLocalError("Schema service is not available.");
        return;
      }
      if (schemaLoadKeyRef.current === schemaIdToLoad) {
        setLoading(false);
        return;
      }
      schemaLoadKeyRef.current = schemaIdToLoad;
      setLoading(true);
      setLocalError("");
      try {
        const linkedSchema = await loadSchemaById(schemaMgrRef.current, schemaIdToLoad, coreStateRef.current);
        if (!mounted) return;
        setSchema(linkedSchema);
        if (!linkedSchema) {
          setLocalError("The linked Entity Schema could not be loaded.");
          return;
        }
        const savedName = readValue(savedValuesRef.current, "SchemaName", "schemaName", "name");
        const savedDescription = readValue(savedValuesRef.current, "SchemaDescription", "schemaDescription", "desc");
        setForm((previous) => ({
          ...previous,
          SchemaName: savedName || previous.SchemaName || getSchemaDisplayName(linkedSchema),
          SchemaDescription: savedDescription ?? previous.SchemaDescription ?? getValue(linkedSchema, "Description", "description") ?? ""
        }));
      } catch (ex) {
        if (mounted) setLocalError(ex?.message || "Linked Entity Schema could not be loaded.");
      } finally {
        if (mounted) setLoading(false);
      }
    };
    void loadLinkedSchema();
    return () => {
      mounted = false;
    };
  }, [form.SchemaId, schemaManagerReady]);

  useEffect(() => {
    if (mode !== ENTITY_SCHEMA_MODE_DESTINATION_SCHEMA) {
      destinationSchemaLoadKeyRef.current = "";
      setDestinationSchemaLoading(false);
      return undefined;
    }
    if (!destinationSchemaManagerReady) {
      setDestinationSchemaOptions((previous) => previous.length ? [] : previous);
      setDestinationSchemaLoading(false);
      setLocalError("External System resource services are not available.");
      return undefined;
    }

    const loadKey = "entity-schema-destination-options";
    if (destinationSchemaLoadKeyRef.current === loadKey) return undefined;
    destinationSchemaLoadKeyRef.current = loadKey;

    let mounted = true;
    const load = async () => {
      setDestinationSchemaLoading(true);
      setLocalError("");
      try {
        const options = await loadDestinationSchemaOptions({
          externalSystemMgr: externalSystemMgrRef.current,
          entityObjectMgr: entityObjectMgrRef.current,
          resourceConfigurationMgr: resourceConfigurationMgrRef.current,
          coreState: coreStateRef.current
        });
        if (mounted) setDestinationSchemaOptions(options);
      } catch (ex) {
        if (mounted) {
          setDestinationSchemaOptions([]);
          setLocalError(ex?.message || "Destination Schema options could not be loaded.");
        }
      } finally {
        if (mounted) setDestinationSchemaLoading(false);
      }
    };
    void load();
    return () => {
      mounted = false;
    };
  }, [destinationSchemaManagerReady, mode]);

  const updateForm = (fieldName, value) => {
    setForm((previous) => ({ ...previous, [fieldName]: value }));
    onChange?.(fieldName, value);
  };

  const buildStepValues = useCallback((overrides = {}) => {
    const merged = {
      ...formRef.current,
      ...overrides
    };
    const transactionSets = normalizeMultiSelectValue(merged.EdiTransactionSets);
    const destinationSelections = normalizeMultiSelectValue(merged.DestinationSchemaSelections);
    return {
      ...merged,
      EdiTransactionSets: transactionSets,
      DestinationSchemaSelections: destinationSelections,
      SchemaSetupMode: modeRef.current,
      id: overrides.SchemaId || formRef.current.SchemaId,
      name: overrides.SchemaName || formRef.current.SchemaName,
      desc: overrides.SchemaDescription ?? formRef.current.SchemaDescription,
      nsv: overrides.CreateNewSchemaVersion ?? formRef.current.CreateNewSchemaVersion,
      ediformat: overrides.EdiFormat || formRef.current.EdiFormat,
      edidirection: overrides.EdiDirection || formRef.current.EdiDirection,
      ts: transactionSets,
      sm: overrides.EdiSchemaShape || formRef.current.EdiSchemaShape,
      selectedDestinationSchemas: destinationSelections,
      dss: destinationSelections
    };
  }, []);

  const ensureSchema = useCallback(async () => {
    const activeSchemaMgr = schemaMgrRef.current;
    if (!activeSchemaMgr?.saveItem) throw new Error("Schema service is not available.");
    const current = formRef.current;
    const existing = schemaRef.current;
    const schemaToSave = {
      ...(existing || {}),
      _id: existing ? getId(existing) : current.SchemaId || undefined,
      IsNew: !existing && !current.SchemaId,
      Name: current.SchemaName || getSchemaDisplayName(existing) || defaultSchemaName(wizardRef.current),
      Description: current.SchemaDescription || getValue(existing, "Description", "description") || "",
      IsActive: true,
      IsDeleted: false
    };
    const saveResult = await activeSchemaMgr.saveItem(schemaToSave);
    requireSuccessfulServiceResponse(saveResult, "Entity Schema could not be saved.", coreStateRef.current);
    const savedSchema = getOperationItem(saveResult) || schemaToSave;
    const schemaId = getId(savedSchema) || current.SchemaId;
    setSchema(savedSchema);
    setForm((previous) => ({
      ...previous,
      SchemaId: schemaId,
      SchemaName: getValue(savedSchema, "Name", "name") || previous.SchemaName,
      SchemaDescription: getValue(savedSchema, "Description", "description") ?? previous.SchemaDescription
    }));
    return savedSchema;
  }, []);

  const saveSchemaStep = useCallback(async (currentValues = {}) => {
    const merged = buildStepValues(currentValues);
    setForm((previous) => ({ ...previous, ...merged }));
    if (!String(merged.SchemaName || "").trim() && !merged.SchemaId) throw buildWizardValidationError("Enter a Schema name.");
    if (modeRef.current === ENTITY_SCHEMA_MODE_EDI && !normalizeMultiSelectValue(merged.EdiTransactionSets).length) {
      throw buildWizardValidationError("Select at least one EDI Transaction Set.");
    }
    if (modeRef.current === ENTITY_SCHEMA_MODE_DESTINATION_SCHEMA && !normalizeMultiSelectValue(merged.DestinationSchemaSelections).length) {
      throw buildWizardValidationError("Select at least one Destination Schema.");
    }
    const savedSchema = await ensureSchema();
    const schemaId = getId(savedSchema) || merged.SchemaId;
    return buildStepValues({
      ...merged,
      SchemaId: schemaId,
      id: schemaId
    });
  }, [buildStepValues, ensureSchema]);

  useEffect(() => {
    registerStepController?.({
      skipStepInputValidation: true,
      prepareSave: saveSchemaStep,
      prepareContinue: saveSchemaStep
    });
    return () => registerStepController?.(null);
  }, [registerStepController, saveSchemaStep]);

  const stepDescription = mode === ENTITY_SCHEMA_MODE_EDI
    ? "Define the linked Entity Schema record and EDI generation settings. EDI Entity Objects are generated after Source Object selection so Source Object of Record links can be applied."
    : mode === ENTITY_SCHEMA_MODE_DESTINATION_SCHEMA
      ? "Define the linked Entity Schema record and select External System destination schemas to generate Entity Objects and fields. Source Object of Record links can be assigned in the next step."
      : "Define the linked Entity Schema record for this Resource Configuration.";
  const modeLabel = getEntitySchemaModeLabel(wizard, mode);

  return (
    <div className="resource-wizard__linked-step">
      <LinkedStepHeader title={`Entity Schema: ${modeLabel}`} description={stepDescription} />
      {localError ? <div className="resource-wizard__inline-error">{localError}</div> : null}
      {loading ? (
        <div className="resource-wizard__linked-step-loading"><Loading /></div>
      ) : (
        <div className="resource-wizard__fields">
        <div className="resource-wizard__field">
          <FieldControl
            className="resource-wizard__field-control"
            fieldMetadata={fieldMetadata({
              fieldName: "SchemaName",
              displayName: "Schema Name",
              fieldType: SystemTypes.UIFieldType.TextBox
            })}
            value={form.SchemaName}
            disabled={readOnly || saving}
            groupId="entitySchemaConfigurator"
            onChange={(nextValue) => updateForm("SchemaName", fieldValue(nextValue))}
            disableError
          />
        </div>
        <div className="resource-wizard__field">
          <FieldControl
            className="resource-wizard__field-control"
            fieldMetadata={fieldMetadata({
              fieldName: "SchemaDescription",
              displayName: "Schema Description",
              fieldType: SystemTypes.UIFieldType.TextArea
            })}
            value={form.SchemaDescription}
            disabled={readOnly || saving}
            groupId="entitySchemaConfigurator"
            onChange={(nextValue) => updateForm("SchemaDescription", fieldValue(nextValue))}
            disableError
          />
        </div>
        {mode === ENTITY_SCHEMA_MODE_EDI ? (
          <>
            <div className="resource-wizard__field">
              <FieldControl
                className="resource-wizard__field-control"
                fieldMetadata={fieldMetadata({
                  fieldName: "EdiFormat",
                  displayName: "EDI Format",
                  fieldType: SystemTypes.UIFieldType.SelectList,
                  options: [option("X12")]
                })}
                selectData={[option("X12")]}
                value={form.EdiFormat}
                disabled={readOnly || saving}
                groupId="entitySchemaConfigurator"
                onChange={(nextValue) => updateForm("EdiFormat", fieldValue(nextValue))}
                disableError
              />
            </div>
            <div className="resource-wizard__field">
              <FieldControl
                className="resource-wizard__field-control"
                fieldMetadata={fieldMetadata({
                  fieldName: "EdiDirection",
                  displayName: "Direction",
                  fieldType: SystemTypes.UIFieldType.SelectList,
                  options: [option("Outbound"), option("Inbound"), option("Bidirectional")]
                })}
                selectData={[option("Outbound"), option("Inbound"), option("Bidirectional")]}
                value={form.EdiDirection}
                disabled={readOnly || saving}
                groupId="entitySchemaConfigurator"
                onChange={(nextValue) => updateForm("EdiDirection", fieldValue(nextValue))}
                disableError
              />
            </div>
            <div className="resource-wizard__field">
              <FieldControl
                className="resource-wizard__field-control"
                fieldMetadata={fieldMetadata({
                  fieldName: "EdiTransactionSets",
                  displayName: "Transaction Sets",
                  fieldType: SystemTypes.UIFieldType.MultiSelectList,
                  options: ediTransactionSetOptions,
                  placeHolder: "Select one or more transaction sets",
                  isRequired: true
                })}
                selectData={ediTransactionSetOptions}
                value={form.EdiTransactionSets}
                disabled={readOnly || saving}
                groupId="entitySchemaConfigurator"
                onChange={(nextValue) => updateForm("EdiTransactionSets", fieldValue(nextValue))}
                disableError
              />
            </div>
            <div className="resource-wizard__field">
              <FieldControl
                className="resource-wizard__field-control"
                fieldMetadata={fieldMetadata({
                  fieldName: "EdiSchemaShape",
                  displayName: "Schema Shape",
                  fieldType: SystemTypes.UIFieldType.SelectList,
                  options: [option("All"), option("Flat"), option("HeaderLine")]
                })}
                selectData={[option("All"), option("Flat"), option("HeaderLine")]}
                value={form.EdiSchemaShape}
                disabled={readOnly || saving}
                groupId="entitySchemaConfigurator"
                onChange={(nextValue) => updateForm("EdiSchemaShape", fieldValue(nextValue))}
                disableError
              />
            </div>
          </>
        ) : null}
        {mode === ENTITY_SCHEMA_MODE_DESTINATION_SCHEMA ? (
          <div className="resource-wizard__field">
            {destinationSchemaLoading ? (
              <div className="resource-wizard__linked-step-loading"><Loading /></div>
            ) : (
              <FieldControl
                className="resource-wizard__field-control"
                fieldMetadata={fieldMetadata({
                  fieldName: "DestinationSchemaSelections",
                  displayName: "Destination Schemas",
                  fieldType: SystemTypes.UIFieldType.MultiSelectList,
                  options: destinationSchemaOptions,
                  placeHolder: "Select one or more destination schemas",
                  isRequired: true,
                  description: "Select the External System destination schema entity objects to generate into this Entity Schema."
                })}
                selectData={destinationSchemaOptions}
                value={form.DestinationSchemaSelections}
                disabled={readOnly || saving || destinationSchemaLoading}
                groupId="entitySchemaConfigurator"
                onChange={(nextValue) => updateForm("DestinationSchemaSelections", normalizeMultiSelectValue(nextValue))}
                disableError
              />
            )}
          </div>
        ) : null}
        </div>
      )}
      <div className="resource-wizard__linked-step-actions">
        <div />
        <div className="resource-wizard__linked-step-actions-right">
          <ButtonControl type="standard-small" className="resource-wizard__button resource-wizard__button--secondary" onClick={() => onSave?.()} disabled={readOnly || saving || loading || destinationSchemaLoading} loading={saving}>Save</ButtonControl>
          <ButtonControl type="ok-small" className="resource-wizard__button resource-wizard__button--primary" onClick={() => onContinue?.()} disabled={readOnly || saving || loading || destinationSchemaLoading} loading={saving}>Save & Continue</ButtonControl>
        </div>
      </div>
    </div>
  );
};

export default EntitySchemaConfiguratorStep;
